import React, {useEffect, useState} from "react";
import {Box, Typography, useMediaQuery} from "@mui/material";
import {useTranslation} from "react-i18next";
import CookieConsent, {Cookies} from "react-cookie-consent";
import {LibertyTheme} from '../../tokens/libertyTheme';
import {initGA, updateGA} from "../googleAnalytics/googleAnalyticsTracker";
import {saveCookieConsent} from "../../services/AuthenticationService";
import CookieDialog from "./CookieDialog";
import ReactDOM from 'react-dom';
import {PrimarySmallButton} from "../../components/buttons/mainButton";
import {initGTM} from "../googleAnalytics/googleTagManagerTracker";
import Logo from "../../assets/logo.svg";

interface CookieElementProps {
    openCookieDialog: boolean;
    setOpenCookieDialog: (openCookieDialog: boolean) => void;
    setGaCookie: (gaCookie: boolean) => void;
}

export default function CookiesElement(props: CookieElementProps): JSX.Element {
  const {t} = useTranslation();
  const {openCookieDialog, setOpenCookieDialog, setGaCookie} = props;

  const [showCookieBanner, setShowCookieBanner] = useState<boolean>(false);

  const breakpoint = useMediaQuery('(min-width:560px)');

  function waitForElement(selector: any, callback: any): void {
    const el = document.querySelector(selector);
    if (el) return callback(el);
  
    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        observer.disconnect();
        callback(el);
      }
    });
  
    observer.observe(document.body, {childList: true, subtree: true});
  }

  waitForElement('.btn-wrapper', (element: any) => {
    if (!element.querySelector('.react-btn-wrapper')) {
      const reactContainer = document.createElement('div');
      reactContainer.className = 'react-btn-wrapper';
      element.appendChild(reactContainer);
  
      ReactDOM.render(
        <PrimarySmallButton
          label={t("cookie.popup_button")}
          action={(): void => setOpenCookieDialog(true)}
        />,
        reactContainer
      );
    }
  });

  const handleAcceptCookie = (stripe: boolean, analysis: boolean, ga?: boolean, gtm?: boolean): void => {
    let initGa = false; let initGtm = false; let initStripe = false;         

    if (openCookieDialog) {
      if (stripe) {
        initStripe = true;
      }
      if (analysis) {
        initGa = true; initGtm = true;
      } else {
        if (ga) {
          initGa = true;
        }
        if (gtm) {
          initGtm = true;
        }
      }
    } else {
      initGa = true; initGtm = true; initStripe = true;
    }

    Cookies.set("joozyCookieConsent", {"ga": initGa, "gtm": initGtm, "stripe": initStripe}, {path: "/", expires: 365});

    if (initGa) {
      if (process.env.REACT_APP_GOOGLE_ANALYTICS_TRACKING_ID) {
        updateGA();
        saveCookieConsent();
      }
    } else {      
      Cookies.remove("_ga", {domain: process.env.REACT_APP_COOKIE_GA_DOMAIN, path: process.env.REACT_APP_COOKIE_PATH});
      Cookies.remove("_ga_"+process.env.REACT_APP_GOOGLE_ANALYTICS_TRACKING_ID!.split("-")[1], {domain: process.env.REACT_APP_COOKIE_GA_DOMAIN, path: process.env.REACT_APP_COOKIE_PATH});
      setGaCookie(false);
    }

    if (initGtm) {    
      initGTM();
    } else {
      Cookies.remove("_gcl_au", {domain: process.env.REACT_APP_COOKIE_GTM_DOMAIN, path: process.env.REACT_APP_COOKIE_PATH});
    }

    if (!initStripe) {
      Cookies.remove("__stripe_mid", {domain: process.env.REACT_APP_COOKIE_STRIPE_DOMAIN, path: process.env.REACT_APP_COOKIE_PATH});
      Cookies.remove("__stripe_sid", {domain: process.env.REACT_APP_COOKIE_STRIPE_DOMAIN, path: process.env.REACT_APP_COOKIE_PATH});
    }

    setOpenCookieDialog(false);
  };

  const handleDeclineCookie = (): void => {
    Cookies.remove("_ga", {domain: process.env.REACT_APP_COOKIE_GA_DOMAIN, path: process.env.REACT_APP_COOKIE_PATH});
    Cookies.remove("_ga_"+process.env.REACT_APP_GOOGLE_ANALYTICS_TRACKING_ID!.split("-")[1], {domain: process.env.REACT_APP_COOKIE_GA_DOMAIN, path: process.env.REACT_APP_COOKIE_PATH});
    Cookies.remove("_gcl_au", {domain: process.env.REACT_APP_COOKIE_GTM_DOMAIN, path: process.env.REACT_APP_COOKIE_PATH});
    Cookies.remove("__stripe_mid", {domain: process.env.REACT_APP_COOKIE_STRIPE_DOMAIN, path: process.env.REACT_APP_COOKIE_PATH});
    Cookies.remove("__stripe_sid", {domain: process.env.REACT_APP_COOKIE_STRIPE_DOMAIN, path: process.env.REACT_APP_COOKIE_PATH});
    setGaCookie(false);
  };

  useEffect(() => {
    let isConsent = Cookies.get('joozyCookieConsent');  
    
    if (process.env.REACT_APP_GOOGLE_ANALYTICS_TRACKING_ID) {
      initGA(process.env.REACT_APP_GOOGLE_ANALYTICS_TRACKING_ID);
    }
            
    if (isConsent !== undefined) {
      isConsent = JSON.parse(isConsent);
      if (!isConsent['ga']) {        
        Cookies.remove("_ga", {domain: process.env.REACT_APP_COOKIE_GA_DOMAIN, path: process.env.REACT_APP_COOKIE_PATH});
        Cookies.remove("_ga_"+process.env.REACT_APP_GOOGLE_ANALYTICS_TRACKING_ID!.split("-")[1], {domain: process.env.REACT_APP_COOKIE_GA_DOMAIN, path: process.env.REACT_APP_COOKIE_PATH});
      }
      if (!isConsent['gtm']) {        
        Cookies.remove("_gcl_au", {domain: process.env.REACT_APP_COOKIE_GTM_DOMAIN, path: process.env.REACT_APP_COOKIE_PATH});
      } else {
        initGTM();
      }
      if (!isConsent['stripe']) {     
        Cookies.remove("__stripe_mid", {domain: process.env.REACT_APP_COOKIE_STRIPE_DOMAIN, path: process.env.REACT_APP_COOKIE_PATH});
        Cookies.remove("__stripe_sid", {domain: process.env.REACT_APP_COOKIE_STRIPE_DOMAIN, path: process.env.REACT_APP_COOKIE_PATH});
      }
    } else {
      Cookies.remove("__stripe_mid", {domain: `.${window.location.hostname}`, path: process.env.REACT_APP_COOKIE_PATH});
      Cookies.remove("__stripe_sid", {domain: `.${window.location.hostname}`, path: process.env.REACT_APP_COOKIE_PATH});
      Cookies.remove("joozyBoCookie", {domain: `${window.location.hostname}`, path: process.env.REACT_APP_COOKIE_PATH});
      Cookies.remove("_gcl_au", {domain: process.env.REACT_APP_COOKIE_GTM_DOMAIN, path: process.env.REACT_APP_COOKIE_PATH});
      Cookies.remove("_ga", {domain: process.env.REACT_APP_COOKIE_GA_DOMAIN, path: process.env.REACT_APP_COOKIE_PATH});
      Cookies.remove("_ga_K4BDF328X8", {domain: process.env.REACT_APP_COOKIE_GA_DOMAIN, path: process.env.REACT_APP_COOKIE_PATH});
    }
  }, [])

  useEffect(() => {
    const isConsent = Cookies.get('joozyCookieConsent');    
            
    if (isConsent !== undefined) {
      setShowCookieBanner(false);
    } else {
      setShowCookieBanner(true);
    }
  }, [Cookies.get('joozyCookieConsent')])

  return (
    <Box sx={{".btn-wrapper": {height: "70px", margin: "30px 0 0 0", display: "flex", flexWrap: "wrap", justifyContent: "space-between", width: "100%"}, ".react-btn-wrapper": {height: "30px", margin: "10px 0 0 0", width: "100%", "button": {backgroundColor: "ornament.main", width: "100%", fontWeight: 600, fontSize: "17px", heigth: "auto", color: "primary.main"}}}}>
      <CookieDialog open={openCookieDialog} close={(): void => setOpenCookieDialog(false)} handleAcceptCookie={handleAcceptCookie} />
      {showCookieBanner && <CookieConsent
        location="bottom"
        buttonText={t("cookie.button")}
        declineButtonText={t("cookie.decline_button")}
        cookieName="joozyCookieConsent"
        cookieValue={{"ga": true, "gtm": true, "stripe": true}}
        declineCookieValue={{"ga": false, "gtm": false, "stripe": false}}
        onAccept={(): void => handleAcceptCookie(true, true)}
        onDecline={handleDeclineCookie}
        enableDeclineButton
        expires={180}
        buttonWrapperClasses={"btn-wrapper"}
        style={{
          backgroundColor: LibertyTheme.palette.landing.main,
          padding: `${LibertyTheme.spacing(7)} ${LibertyTheme.spacing(5)}`,
          boxSizing: "border-box",
          maxWidth: breakpoint ? "500px" : "unset",
          width: breakpoint ? "100%" : "calc(100% - 17px)",
          height: "fit-content",
          alignItems: "center",
          flexWrap: "unset",
          flexDirection: "column",
          right: breakpoint ? "0px" : "unset",
          left: "unset",
          margin: breakpoint ? "0 32px 24px 0" : "0 0 0 0",
          borderRadius: breakpoint ? "10px" : "0px",
          lineHeight: "12px"
        }}
        contentStyle={{
          margin: 0,
          flex: 1
        }}
        buttonStyle={{
          color: LibertyTheme.palette.ornament.main,
          backgroundColor: LibertyTheme.palette.primaryDark.main,
          borderRadius: 4,
          margin: 0,
          fontFamily: LibertyTheme.typography.fontFamily,
          fontWeight: 700,
          fontSize: "18px",
          width: "48%",
          minWidth: "100px",
          height: "auto"
        }}
        declineButtonStyle={{
          color: LibertyTheme.palette.ornament.main,
          backgroundColor: LibertyTheme.palette.primary.main,
          borderRadius: 4,
          margin: "0 12px 0 0",
          fontFamily: LibertyTheme.typography.fontFamily,
          fontWeight: 700,
          fontSize: "18px",
          width: "48%",
          minWidth: "100px",
          height: "auto"
        }}
      >
        <Box sx={{display: "flex", flexDirection: "column", height: "fit-content", alignItems: "center"}}>
          <Box sx={{width: "100%", display: "flex", alignItems: "center", mb: "30px"}}>
            {/* @ts-ignore */}
            <img src={Logo} alt={t("landing_page.alt.circle")} style={{height: "40px", marginRight: "12px"}} />
            <Typography variant="h1" color="secondary.main" sx={{width: "100%", fontWeight: 600, fontSize: "24px", lineHeight: "26px"}}>{t("cookie.title")}</Typography>
          </Box>
          <Typography variant="body1" color="secondary.main" sx={{width: "100%", fontWeight: 600, fontSize: "20px", textAlign: "justify", lineHeight: "26px"}}>{t("cookie.content")}</Typography>
        </Box>
      </CookieConsent>}
    </Box>
  )
}
